import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import { isSchemaInfraError } from '@/lib/supabase-helpers'
import type {
  AgentConfig,
  AgentEvent,
  AgentRole,
  AgentVillageStatus,
  AgentVillageMultiStatus,
  AgentStatus,
} from '../types'

// ─── Status Derivation (Pitfall 2: deterministic status from events) ────

const VALID_STATUSES = new Set<AgentStatus>(['active', 'idle', 'error', 'offline'])
const FIVE_MINUTES_MS = 5 * 60 * 1000

/**
 * Derive agent status from a list of events (newest first).
 * Pure function — no side effects.
 */
export function deriveAgentStatus(events: AgentEvent[]): AgentStatus {
  if (events.length === 0) return 'offline'

  const latest = events[0]

  // Error event → error status
  if (latest.event_type === 'error') return 'error'

  // Status event with explicit status in payload
  if (latest.event_type === 'status' && latest.payload?.status) {
    const payloadStatus = String(latest.payload.status) as AgentStatus
    return VALID_STATUSES.has(payloadStatus) ? payloadStatus : 'offline'
  }

  // Check if latest event is within 5 minutes → active
  const eventAge = Date.now() - new Date(latest.created_at).getTime()
  if (eventAge < FIVE_MINUTES_MS) return 'active'

  return 'idle'
}

// ─── Time Formatting ────────────────────────────────────────────────────

/**
 * Format an ISO timestamp as relative time: "Xs ago", "Xm ago", "Xh ago", "Xd ago"
 */
export function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const seconds = Math.floor(diffMs / 1000)

  if (seconds < 1) return 'just now'
  if (seconds < 60) return `${seconds}s ago`

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

/**
 * Build a 1-line action summary: "3m ago: product price update"
 */
export function buildLastActionSummary(event: AgentEvent): string {
  const time = formatRelativeTime(event.created_at)
  const description = (event.payload?.description as string) || event.event_type
  return `${time}: ${description}`
}

// ─── Data Fetching ──────────────────────────────────────────────────────

/**
 * Fetch agent status for a batch of app IDs.
 * Groups events by app_id, derives status per group.
 */
export async function fetchAgentStatusBatch(
  appIds: string[],
): Promise<Map<string, AgentVillageStatus>> {
  const result = new Map<string, AgentVillageStatus>()
  if (appIds.length === 0) return result

  const { data, error } = await supabase
    .schema('cogo')
    .from('agent_events')
    .select('*')
    .in('app_id', appIds)
    .order('created_at', { ascending: false })
    .limit(5 * appIds.length)

  if (error) {
    if (isSchemaInfraError(error)) {
      console.warn(`[cogo-schema] ${error.code}: ${error.message} — returning empty agent status batch`)
      return result
    }
    console.error('[agent-village] fetchAgentStatusBatch error:', error.message)
    return result
  }

  // Group events by app_id
  const grouped = new Map<string, AgentEvent[]>()
  for (const row of data ?? []) {
    const event = mapRecordToAgentEvent(row)
    if (!event.app_id) continue
    const existing = grouped.get(event.app_id) ?? []
    existing.push(event)
    grouped.set(event.app_id, existing)
  }

  // Derive status for each app
  for (const appId of appIds) {
    const events = grouped.get(appId) ?? []
    const status = deriveAgentStatus(events)
    const latestEvent = events[0] ?? null

    result.set(appId, {
      app_id: appId,
      status,
      last_action: latestEvent
        ? (latestEvent.payload?.description as string) || latestEvent.event_type
        : null,
      last_event_at: latestEvent?.created_at ?? null,
    })
  }

  return result
}

// ─── Realtime Subscription (Pitfall 1: single channel) ──────────────────

/**
 * Subscribe to village-wide agent status updates via a SINGLE Supabase Realtime channel.
 * Uses project_id-based channel name to avoid the 10 channel limit.
 */
export function subscribeToVillageStatus(
  projectId: string,
  onUpdate: (status: AgentVillageStatus) => void,
): { unsubscribe: () => void } {
  // No Supabase in this deployment (DGX declares none): opening a channel here
  // reaches the `.invalid` sentinel host over WebSocket, which the fail-fast
  // `fetch` in lib/supabase.ts cannot intercept. Hand back an inert handle so
  // the caller's contract is unchanged and no socket is attempted.
  if (!isSupabaseConfigured) return { unsubscribe: () => {} }

  const channel = supabase
    .channel(`village-status:${projectId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'cogo',
        table: 'agent_events',
      },
      (payload) => {
        const record = payload.new as Record<string, unknown>
        const event = mapRecordToAgentEvent(record)
        const status = deriveAgentStatus([event])

        onUpdate({
          app_id: event.app_id ?? '',
          status,
          last_action: (event.payload?.description as string) || event.event_type,
          last_event_at: event.created_at,
        })
      },
    )
    .subscribe()

  return {
    unsubscribe: () => {
      supabase.removeChannel(channel)
    },
  }
}

// ─── Multi-Agent Config Fetching ─────────────────────────────────────────

/**
 * List all agent configs for an app via list_agent_configs RPC.
 * Returns configs sorted by agent_role for deterministic tab ordering.
 */
export async function listAgentConfigs(
  appId: string,
): Promise<AgentConfig[]> {
  const { data, error } = await supabase
    .schema('cogo')
    .rpc('list_agent_configs', { p_app_id: appId })

  if (error) {
    if (isSchemaInfraError(error)) {
      console.warn(`[cogo-schema] ${error.code}: ${error.message} — returning empty agent configs`)
      return []
    }
    console.error('[agent-village] listAgentConfigs error:', error.message)
    return []
  }

  return (data ?? []) as AgentConfig[]
}

/**
 * Get multi-agent village status for a set of apps.
 * Groups per-agent statuses by app_id, with per-role breakdown.
 */
export async function getMultiAgentVillageStatus(
  appIds: string[],
): Promise<AgentVillageMultiStatus[]> {
  if (appIds.length === 0) return []

  // Fetch all configs for these apps
  const { data: configs, error: configError } = await supabase
    .schema('cogo')
    .from('agent_configs')
    .select('app_id, agent_role')
    .in('app_id', appIds)

  if (configError) {
    if (isSchemaInfraError(configError)) {
      console.warn(`[cogo-schema] ${configError.code}: ${configError.message} — returning empty multi-agent status`)
      return []
    }
    console.error('[agent-village] getMultiAgentVillageStatus config error:', configError.message)
    return []
  }

  // Fetch latest events for status derivation
  const statusBatch = await fetchAgentStatusBatch(appIds)

  // Group configs by app_id and build multi-status
  const appMap = new Map<string, AgentVillageMultiStatus>()

  for (const appId of appIds) {
    appMap.set(appId, { app_id: appId, agents: [] })
  }

  for (const cfg of configs ?? []) {
    const appId = cfg.app_id as string
    const role = cfg.agent_role as AgentRole
    const entry = appMap.get(appId)
    if (!entry) continue

    const villageStatus = statusBatch.get(appId)

    entry.agents.push({
      agent_role: role,
      status: villageStatus?.status ?? 'offline',
      last_action: villageStatus?.last_action ?? null,
      last_event_at: villageStatus?.last_event_at ?? null,
    })
  }

  return Array.from(appMap.values())
}

// ─── Helpers ────────────────────────────────────────────────────────────

function mapRecordToAgentEvent(record: Record<string, unknown>): AgentEvent {
  return {
    id: String(record.id ?? ''),
    agent_id: record.agent_id != null ? String(record.agent_id) : null,
    app_id: record.app_id != null ? String(record.app_id) : null,
    event_type: String(record.event_type ?? ''),
    severity: (record.severity as AgentEvent['severity']) ?? 'info',
    action_category: (record.action_category as AgentEvent['action_category']) ?? null,
    reversible: Boolean(record.reversible),
    undo_payload: (record.undo_payload as Record<string, unknown>) ?? null,
    duration_ms: record.duration_ms != null ? Number(record.duration_ms) : null,
    payload: (record.payload as Record<string, unknown>) ?? {},
    created_at: String(record.created_at ?? ''),
  }
}
